/**
 * DataCacheServiceCard - карточка быстрой очистки временных данных.
 * Выдержана в единой стилистике страницы Профиля (фон #f8fafc, четкая кнопка).
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tooltip
} from '@mui/material';
import {
  StorageOutlined,
  RestartAlt,
  CheckCircleOutline
} from '@mui/icons-material';
import { ZERDE_COLORS } from '../../constants';
import { notify } from '../../../notifications';
import { serviceButtonFullSx, serviceRowSx } from './serviceCardStyles';

const CACHE_KEYS = [
  'monitoring_cash_table_fields',
  'periodReportData',
  'userCheckData',
  'compare_selected_date',
  'oj_col_vis',
  'oj_col_order'
];

const DataCacheServiceCard: React.FC = () => {
  const [clearing, setClearing] = useState(false);
  const [cleared, setCleared] = useState(false);

  const handleClearTablesCache = () => {
    setClearing(true);
    try {
      CACHE_KEYS.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {
          // Игнорируем
        }
      });
      setCleared(true);
      notify.success('Временные данные успешно очищены');
      setTimeout(() => setCleared(false), 3000);
    } catch {
      notify.error('Не удалось очистить данные');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Box sx={serviceRowSx}>
      {/* Левая часть: Иконка + Заголовок + Простое объяснение */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            bgcolor: 'rgba(243, 136, 58, 0.1)',
            color: ZERDE_COLORS.orange,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <StorageOutlined sx={{ fontSize: 24 }} />
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
            Очистка временных данных (кэша)
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontSize: '0.85rem' }}>
            Нажмите, если в таблицах сбились колонки, завис журнал или не открываются отчеты. Ваш вход и пароль останутся на месте.
          </Typography>
        </Box>
      </Box>

      {/* Правая часть: Кнопка действия */}
      <Box sx={{ flexShrink: 0 }}>
        <Tooltip title="Сбросить сохраненный вид таблиц до стандартного" arrow>
          <Button
            variant="outlined"
            startIcon={cleared ? <CheckCircleOutline /> : <RestartAlt />}
            disabled={clearing}
            onClick={handleClearTablesCache}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              py: 0.85,
              px: 2,
              borderColor: cleared ? '#86efac' : '#cbd5e1',
              bgcolor: cleared ? '#f0fdf4' : '#ffffff',
              color: cleared ? '#16a34a' : '#334155',
              whiteSpace: 'nowrap',
              ...serviceButtonFullSx,
              '&:hover': {
                borderColor: '#ea580c',
                bgcolor: '#fff7ed',
                color: '#c2410c'
              }
            }}
          >
            {clearing ? 'Очистка...' : cleared ? 'Очищено!' : 'Очистить временные данные'}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default DataCacheServiceCard;
